import React from 'react';

/**
 * All stored payment form refs.
 * Each is associated with a specific payment type.
 */
const paymentFormRefMap = new Map<string, React.RefObject<any>>();

/**
 * Define a dynamic ref for a payment form based on the payment method key.
 * Note that this overwrites any existing ref for the same key.
 */
const definePaymentFormRef = <T>(key: string): React.RefObject<T> => {
  const ref = React.createRef<T>();
  paymentFormRefMap.set(key, ref);
  return ref;
};

/**
 * Read a dynamic ref for a payment form based on the payment method key.
 *
 * The returned ref can be used to trigger functions for the payment type,
 * such as submit (`formRef.current?.submit()`) and validate, if the form
 * component it was passed down to attached functions to it using
 * `useImperativeHandle`.
 *
 * Returns undefined if the ref for the key does not exist.
 */
const readPaymentFormRef = <T>(key: string): React.RefObject<T> | undefined =>
  paymentFormRefMap.get(key) as React.RefObject<T>;

/**
 * Define and read dynamic refs for payment forms, where a single ref
 * corresponds to a single payment method.
 *
 * Each ref gets forwarded down the UI until the payment form component (or a
 * component in it) attaches functions to the ref with `useImperativeHandle`.
 *
 * Functions attached this way can then be called from the parent component
 * (PaymentElement) to trigger actions on the payment form, such as via
 * `formRef.current?.submit()`.
 */
export const useDynamicRefs = <T>(): [
  typeof definePaymentFormRef<T>,
  typeof readPaymentFormRef<T>,
] => [definePaymentFormRef, readPaymentFormRef];
